import { useMemo, useCallback } from "react"

import { useParams } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { useRoleIsReadOnly } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useAlertsDispatchContext } from "@l2r-front/l2r-query"
import { MoreMenu, Typography, useIsMobileDevice } from "@l2r-front/l2r-ui"
import { formatNumber, formatMetersToDistance, formatDate } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { STATUS_DONE, STATUSES, statusesSettings } from "../../constants/status"
import { useRoadworkDelete } from "../../hooks/mutations/roadwork/useRoadworkDelete"
import { computeRoadworkLength } from "../../utils/roadworkUtils"

import * as Styled from "./TechniqueItemCard.styled"

export function TechniqueItemCard(props) {

    const {
        currency,
        doneAt,
        handleClick,
        handleComplete,
        handleGoToDetails,
        technique,
        status,
        isSelected,
        isDisabled,
        ...otherProps
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)
    const readOnly = useRoleIsReadOnly()
    const { setAlert } = useAlertsDispatchContext()
    const isMobile = useIsMobileDevice()
    const { road } = useParams()

    const deleteMutation = useRoadworkDelete(technique, {}, {
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.roadworkCard.snackbar.deleteError"),
        successSnackbarMessage: t(I18N_NAMESPACE, "containers.roadworkCard.snackbar.deleteSuccess"),
    })

    const formattedTechnique = useMemo(() => {
        const techniqueLength = computeRoadworkLength(technique.linearLocation)
        return ({
            ...technique,
            cost: formatNumber(technique.cost),
            impact: technique.impact,
            length: technique.linearLocation ? formatMetersToDistance(techniqueLength) : null,
        })
    }, [technique])

    const {
        acronym,
        cost,
        impact,
        length,
        year,
    } = formattedTechnique

    const isDeletable = useMemo(() => {
        return status !== STATUS_DONE && !readOnly
    }, [readOnly, status])

    const handleDeleteEquipment = useCallback(() => {
        setAlert({
            alertTitle: t(I18N_NAMESPACE, "containers.techniqueItemCard.delete.confirmation"),
            cancelText: t(I18N_NAMESPACE, "containers.techniqueItemCard.delete.cancel"),
            confirmText: t(I18N_NAMESPACE, "containers.techniqueItemCard.delete.confirm"),
            callback: () => {
                deleteMutation.mutate()
            },
            severity: "warning",
        })
    }, [deleteMutation, setAlert, t])

    const handleDetailsButtonClick = useCallback(() => {
        handleGoToDetails()
    }, [handleGoToDetails])

    const displayButtonsZone = handleGoToDetails || handleComplete

    return <Styled.Card
        role="button"
        elevation={0}
        {...otherProps}
        onClick={handleClick}
        disabled={isDisabled}
        selected={isSelected}>
        <Styled.ContentWrapper>
            <Styled.BadgeWrapper>
                {doneAt ? <Styled.DoneAtWrapper>
                    <Typography variant="Small">{!road ? doneAt : formatDate(doneAt, isMobile ? "yyyy" : "dd.MM.yyyy")}</Typography>
                </Styled.DoneAtWrapper> :
                    <Typography variant="Small">{year}</Typography>
                }
                <Styled.HeaderRight>
                    <Styled.ColoredBadge id={"impact-badge"} impact={impact} />
                    {handleGoToDetails && <MoreMenu
                        color="tertiary"
                        menuConfig={[
                            {
                                id: "details-navigation",
                                enabled: true,
                                label: t(I18N_NAMESPACE, "containers.techniqueItemCard.menu.details"),
                                props: {
                                    onClick: handleDetailsButtonClick,
                                },
                            },
                            {
                                enabled: !!isDeletable,
                                id: "edit-delete",
                                label: t(I18N_NAMESPACE, "containers.techniqueItemCard.menu.delete"),
                                props: {
                                    onClick: handleDeleteEquipment,
                                },
                            },

                        ].filter(Boolean)}
                    />}
                </Styled.HeaderRight>
            </Styled.BadgeWrapper>
            <Styled.Element>
                <Typography variant="Small accent">{acronym}</Typography>
            </Styled.Element>
            <Styled.BottomElementsWrapper>
                {length && <Styled.Element>
                    <Styled.TextWrapper>
                        <Typography variant="H3">{t(I18N_NAMESPACE, `components.techniqueCardItem.${isMobile ? "lengthMobile" : "length"}`)}</Typography>
                        <Typography variant="Regular">{length}</Typography>
                    </Styled.TextWrapper>
                </Styled.Element>}
                <Styled.Element>
                    <Styled.TextWrapper>
                        <Typography variant="H3">{t(I18N_NAMESPACE, "components.techniqueCardItem.cost")}</Typography>
                        <Styled.Cost variant="Regular">{`${cost} ${currency}`}</Styled.Cost>
                    </Styled.TextWrapper>
                </Styled.Element>
            </Styled.BottomElementsWrapper>
        </Styled.ContentWrapper>
        {displayButtonsZone && <Styled.Element>
            <Styled.ButtonsWrapper>
                <Styled.StatusChip label={t(I18N_NAMESPACE, statusesSettings[status].statusLabel)} />
            </Styled.ButtonsWrapper>
        </Styled.Element>}
    </Styled.Card>
}

TechniqueItemCard.defaultProps = {
    isDisabled: false,
    isSelected: false,
}

TechniqueItemCard.propTypes = {
    currency: PropTypes.string.isRequired,
    doneAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    handleClick: PropTypes.func.isRequired,
    handleGoToDetails: PropTypes.func,
    isDisabled: PropTypes.bool,
    isSelected: PropTypes.bool,
    technique: PropTypes.technique.isRequired,
    status: PropTypes.oneOf(STATUSES),
}
